import { ApolloError, useMutation } from '@apollo/client';
import { Button } from '@fluentui/react-components';
import React, { useContext } from 'react';
import { hasAccessForCompany } from '../../../utils/authorization/authorizationCheck';
import UserContext from '../../../utils/authorization/UserContext';
import useToast from '../../../utils/hooks/useToast';
import {
  ANALYZE_DOCUMENTS,
  GET_RUNS_FOR_DOCUMENT,
} from '../../../utils/statApi/DocumentAnalysisApi';
import { StatUser } from '../../../utils/statApi/UsersApi';

interface IAnalyzeButtonProps {
  documentId: number;
  companyId?: number;
  companyCode?: string;
}

const isAuthorizationError = (error: ApolloError): boolean => {
  return (
    error.graphQLErrors?.some(
      (graphQLError) => graphQLError?.extensions?.code === 'AUTH_NOT_AUTHORIZED',
    ) || error.message?.toLowerCase().includes('not authorized')
  );
};

const AnalyzeButton = (props: IAnalyzeButtonProps): JSX.Element | null => {
  const { documentId, companyId, companyCode } = props;
  const user: StatUser = useContext(UserContext);
  const { createToast } = useToast();
  const canAnalyze = hasAccessForCompany(user, 'DocumentAnalysis:Create', {
    companyId,
    companyCode,
  });

  const [analyze, { loading }] = useMutation(ANALYZE_DOCUMENTS, {
    refetchQueries: [GET_RUNS_FOR_DOCUMENT],
  });

  if (!canAnalyze) {
    return null;
  }

  const onClick = async (): Promise<void> => {
    try {
      await analyze({
        variables: {
          createAnalysisRunsDto: {
            documentIds: [documentId],
          },
        },
      });
    } catch (error) {
      const authorizationFailed = error instanceof ApolloError && isAuthorizationError(error);
      createToast({
        id: Date.now(),
        header: authorizationFailed ? 'Not authorized' : 'Analyze failed',
        content: authorizationFailed
          ? 'You do not have permission to analyze this document.'
          : 'Unable to start analysis. Please try again.',
        iconName: 'ErrorBadge',
      });
    }
  };

  return (
    <Button appearance="primary" disabled={loading} onClick={onClick}>
      Analyze
    </Button>
  );
};

export default AnalyzeButton;
